/**
 * SortableList - Accessible, touch-friendly drag-to-reorder list using @dnd-kit
 */

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { memo, useCallback, useState } from 'react';

import type {
  SortableItemContext,
  SortableItemProps,
  SortableListProps,
} from './SortableList.types';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

function SortableItem<T extends { uuid: string }>({
  item,
  renderItem,
  disabled,
}: SortableItemProps<T>): React.JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.uuid, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const context: SortableItemContext = {
    isDragging,
    isOverlay: false,
    handleAttributes: attributes,
    handleListeners: listeners,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {renderItem(item, context)}
    </div>
  );
}

function SortableListInner<T extends { uuid: string }>({
  items,
  onReorder,
  renderItem,
  className = '',
  disabled = false,
}: SortableListProps<T>): React.JSX.Element {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeItem = items.find((item) => item.uuid === activeId);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.uuid === active.id);
        const newIndex = items.findIndex((item) => item.uuid === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newItems = [...items];
          const [removed] = newItems.splice(oldIndex, 1);
          newItems.splice(newIndex, 0, removed);
          onReorder(newItems.map((item) => item.uuid));
        }
      }
    },
    [items, onReorder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const itemIds = items.map((item) => item.uuid);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item) => (
            <SortableItem
              key={item.uuid}
              item={item}
              renderItem={renderItem}
              disabled={disabled}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeItem
          ? renderItem(activeItem, {
              isDragging: true,
              isOverlay: true,
              handleAttributes: undefined,
              handleListeners: undefined,
            })
          : null}
      </DragOverlay>
    </DndContext>
  );
}

export const SortableList = memo(SortableListInner) as typeof SortableListInner;
export default SortableList;
